import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import './GettingStartedPage.css';

const STATUS_BADGE = {
  done: { className: 'badge badge-success', label: 'Done' },
  todo: { className: 'badge badge-warning', label: 'To do' },
  optional: { className: 'badge badge-info', label: 'Optional' },
  blocked: { className: 'badge badge-danger', label: 'Blocked' },
};

function GettingStartedPage() {
  const { user } = useAuth();
  const { data, refresh, setDismissed } = useOnboarding();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const viewing = !!user?.impersonating;

  // Fresh data on every visit; "not available" is shown only after this load has settled
  const isSuperAdmin = user?.role === 'super_admin';
  useEffect(() => {
    let active = true;
    const load = isSuperAdmin ? Promise.resolve() : refresh(); // no checklist for super admins
    load.finally(() => {
      if (active) setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [refresh, isSuperAdmin]);

  const changeDismissed = async (value) => {
    setSaving(true);
    await setDismissed(value);
    setSaving(false);
  };

  return (
    <div className="getting-started-page">
      <div className="gs-header">
        <h1 className="gs-title">
          <ListChecks className="gs-title-icon" aria-hidden="true" />
          Getting started
        </h1>
        <p className="gs-intro">The steps to finish before you can reach your prospects. Each one ticks itself off when it is done.</p>
      </div>

      {!data && loaded && (
        <div className="alert alert-info">The setup checklist is not available right now.</div>
      )}

      {data && (
        <>
          <div className="gs-summary">
            <p className="gs-progress-text">
              {data.required_done} of {data.required_total} required steps done
            </p>
            {!viewing && data.dismissed && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={saving} onClick={() => changeDismissed(false)}>
                Show on dashboard again
              </button>
            )}
            {!viewing && !data.dismissed && !data.complete && (
              <button type="button" className="btn btn-sm gs-quiet-btn" disabled={saving} onClick={() => changeDismissed(true)}>
                Hide from dashboard
              </button>
            )}
          </div>

          {data.complete && (
            <div className="alert alert-success">All set: every required step is done.</div>
          )}

          <ol className="gs-step-list">
            {data.steps.map((s) => {
              const badge = STATUS_BADGE[s.status] || STATUS_BADGE.todo;
              return (
                <li key={s.id} className={`card gs-step gs-step--${s.status}`}>
                  <div className="gs-step-main">
                    <div className="gs-step-head">
                      <h2 className="gs-step-title">{s.title}</h2>
                      <span className={badge.className}>{badge.label}</span>
                    </div>
                    <p className="gs-step-desc">{s.description}</p>
                    <small className="gs-done-when">Done when: {s.done_when}</small>
                  </div>
                  {s.status !== 'done' && (
                    <Link className="btn btn-primary btn-sm gs-step-open" to={s.route}>Open</Link>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}

export default GettingStartedPage;
